/**
 * Delegation on a request (migration 1036; PRD-delegation-module.md, Phase 1).
 *
 * The request form's "How much is delegated" section, the level chip a request carries,
 * the ladder setting, and the card's first step, "Clarify the want", which is saved as a
 * Desired Outcome Brief (the brief already asks those questions) and cited as in play.
 * The level, the grid that suggests it and the lines it needs come from the Bounded
 * Agency model through src/lib/delegation.ts; the server (internal_delegation_terms)
 * stays the authority on what a request may say.
 */
import { useEffect, useState } from 'react';
import { Field, Select, TextArea, TextInput } from '@/components/ds';
import { useUserStore } from '@/stores/prioritization/userStore';
import { useDelegationLadder } from '@/hooks/useDelegationLadder';
import type { RiskBand } from '@/data/boundedAgencyDelegation';
import type { WantDraft } from '@/lib/clarifyTheWant';
import {
  LADDER_LEVELS,
  delegationLevelLabel,
  delegationOfDraft,
  delegationSentence,
  levelCap,
  levelPrompts,
  requiredLines,
  setDelegationLadder,
  type Delegation,
  type DelegationDraft,
  type DelegationLadder,
} from '@/lib/delegation';

/** "Level 5 · Act Within Boundaries", opening to the request's delegation sentence. */
export function DelegationChip({
  delegation, ladder, outcome, doneMeans, proposed = false,
}: {
  delegation: Delegation;
  ladder: DelegationLadder;
  outcome?: string | null;
  doneMeans?: string | null;
  proposed?: boolean;
}) {
  return (
    <details className="mt-1 text-xs" data-testid="delegation-chip">
      <summary className="inline-flex cursor-pointer list-none items-center gap-1 px-1.5 py-0.5 border rounded-full border-[var(--rule)] text-[var(--text-sec)] hover:text-[var(--text-pri)]">
        {delegationLevelLabel(delegation.level, ladder)}{proposed ? ' (proposed)' : ''}
      </summary>
      <p className="mt-1 text-[var(--text-sec)]" data-testid="delegation-sentence">
        {delegationSentence(delegation, ladder, { outcome, doneMeans })}
      </p>
      {delegation.aboveReason && (
        <p className="mt-0.5 text-[var(--text-ter)]">Above what the model recommends because: {delegation.aboveReason}</p>
      )}
    </details>
  );
}

const RISKS: { value: RiskBand; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'high', label: 'High' },
];

function Choice<T extends string | boolean>({ options, value, onChange, name }: {
  options: { value: T; label: string }[]; value: T | null; onChange: (v: T) => void; name: string;
}) {
  return (
    <div role="radiogroup" aria-label={name} className="flex flex-wrap gap-2 text-xs">
      {options.map((o) => (
        <button
          key={String(o.value)}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          onClick={() => onChange(o.value)}
          className={`min-h-[44px] md:min-h-0 px-2 py-1 border ${value === o.value ? 'border-[var(--text-pri)] text-[var(--text-pri)]' : 'border-[var(--rule)] text-[var(--text-ter)]'}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/**
 * How much is delegated: risk and reversibility suggest the level (the model's grid), a
 * person may pick lower freely and higher with a reason, and the level asks for the lines
 * it needs, each prompted from the model's own words.
 */
export function DelegationFieldset({
  value, onChange, ladder, outcome, doneMeans,
}: {
  value: DelegationDraft;
  onChange: (next: DelegationDraft) => void;
  ladder: DelegationLadder;
  outcome?: string | null;
  doneMeans?: string | null;
}) {
  const cap = value.risk && value.reversible !== null ? levelCap(value.reversible, value.risk, ladder) : null;
  const set = (patch: Partial<DelegationDraft>) => {
    const next = { ...value, ...patch };
    // Until someone picks a level, the grid picks it; after, their pick stands.
    if (('risk' in patch || 'reversible' in patch) && next.risk && next.reversible !== null
        && (value.level === null || value.level === cap)) {
      next.level = levelCap(next.reversible, next.risk, ladder);
    }
    // A new level brings its own report line, unless the person wrote their own.
    if (next.level !== value.level && next.level !== null
        && (!value.reportBack.trim() || (value.level !== null && value.reportBack === levelPrompts(value.level).reportBack))) {
      next.reportBack = levelPrompts(next.level).reportBack;
    }
    onChange(next);
  };
  const need = requiredLines(value.level);
  const prompts = value.level ? levelPrompts(value.level) : null;
  const above = cap !== null && value.level !== null && value.level > cap;
  const draft = delegationOfDraft(value, ladder);

  return (
    <div className="border border-[var(--rule)] bg-[var(--surface)] px-3 py-2 space-y-3" data-testid="delegation-fieldset">
      <div className="text-xs font-medium text-[var(--text-pri)]">How much is delegated</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="How risky is it?" required>
          <Choice name="How risky is it?" options={RISKS} value={value.risk} onChange={(risk) => set({ risk })} />
        </Field>
        <Field label="Can it be undone?" required>
          <Choice name="Can it be undone?" options={[{ value: true, label: 'Yes' }, { value: false, label: 'No' }]}
            value={value.reversible} onChange={(reversible) => set({ reversible })} />
        </Field>
      </div>
      <Field
        label="Level"
        required
        hint={cap !== null ? `For this risk the model recommends up to ${delegationLevelLabel(cap, ladder)}.` : 'Say how risky it is and whether it can be undone, and the model suggests one.'}
      >
        <Select
          value={value.level === null ? '' : String(value.level)}
          onChange={(e) => set({ level: e.target.value ? Number(e.target.value) : null })}
          placeholder="Choose a level"
          options={LADDER_LEVELS[ladder].map((l) => ({ value: String(l), label: delegationLevelLabel(l, ladder) }))}
        />
      </Field>
      {above && (
        <Field label="Why go above what the model recommends?" required hint="Kept with the request.">
          <TextInput value={value.aboveReason} onChange={(e) => set({ aboveReason: e.target.value })} />
        </Field>
      )}
      {value.level !== null && (
        <>
          <Field label="You may…" required={need.may} hint={prompts?.may}>
            <TextInput value={value.may} onChange={(e) => set({ may: e.target.value })} />
          </Field>
          <Field label="Do not…" required={need.mayNot} hint={prompts?.mayNot}>
            <TextInput value={value.mayNot} onChange={(e) => set({ mayNot: e.target.value })} />
          </Field>
          <Field label="Come back if…" required={need.comeBackIf} hint={prompts?.comeBackIf}>
            <TextInput value={value.comeBackIf} onChange={(e) => set({ comeBackIf: e.target.value })} />
          </Field>
          <Field label="Report back…">
            <TextInput value={value.reportBack} onChange={(e) => set({ reportBack: e.target.value })} />
          </Field>
          {(value.level >= 6) && (
            <p className="text-xs text-[var(--text-ter)]">
              A level never hands over a Decision Right. If deciding this needs one, it stays with whoever holds it.
            </p>
          )}
        </>
      )}
      {draft && (
        <p className="text-xs text-[var(--text-sec)]" data-testid="delegation-sentence-preview">
          {delegationSentence(draft, ladder, { outcome, doneMeans }, { omitEmpty: false })}
        </p>
      )}
    </div>
  );
}

export function ClarifyTheWant({ value, onChange }: { value: WantDraft; onChange: (next: WantDraft) => void }) {
  const set = (k: keyof WantDraft) => (e: { target: { value: string } }) => onChange({ ...value, [k]: e.target.value });
  return (
    <div className="border border-[var(--rule)] bg-[var(--surface)] px-3 py-2 space-y-2" data-testid="clarify-the-want">
      <div className="text-xs font-medium text-[var(--text-pri)]">Clarify the want</div>
      <p className="text-xs text-[var(--text-ter)]">Saved as a desired outcome brief and kept with the request. How they will know they have it is “Met when”, below.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Who wants it?"><TextInput value={value.who} onChange={set('who')} placeholder="You, the team, a customer" /></Field>
        <Field label="What is wanted?"><TextInput value={value.what} onChange={set('what')} /></Field>
      </div>
      <Field label="What does it get them?"><TextArea rows={2} value={value.getsThem} onChange={set('getsThem')} /></Field>
    </div>
  );
}

/** Settings → Modules: the ladder a workspace shows, for admins (set_delegation_ladder). */
export function DelegationLadderSetting({ workspaceId }: { workspaceId: string }) {
  const can = useUserStore((s) => s.can);
  const current = useDelegationLadder(workspaceId);
  const [ladder, setLadder] = useState<DelegationLadder>(current);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => setLadder(current), [current]);
  if (!can('admin.settings')) return null;
  async function choose(next: DelegationLadder) {
    setSaving(true); setErr(null);
    const res = await setDelegationLadder(workspaceId, next);
    setSaving(false);
    if (!res.ok) { setErr(res.error); return; }
    setLadder(next);
  }
  return (
    <div className="mt-3" data-testid="delegation-ladder-setting">
      <div className="text-xs font-medium text-[var(--text-sec)] mb-1">Ladder</div>
      <Choice
        name="Delegation ladder"
        options={[
          { value: 'seven' as DelegationLadder, label: 'Seven levels' },
          { value: 'five' as DelegationLadder, label: 'Five levels (1, 2, 3, 6, 7)' },
        ]}
        value={ladder}
        onChange={(v) => { if (!saving && v !== ladder) void choose(v); }}
      />
      <p className="mt-1 text-xs text-[var(--text-ter)]">
        Five levels shows Do as I say, Research the topic, Make a recommendation, Tell me what you did, and It’s in your hands. Requests already made keep their level.
      </p>
      {err && <div className="text-xs text-[var(--coral)] mt-1">{err}</div>}
    </div>
  );
}
